// Adds left/right arrow buttons around a horizontally scrolling element, so
// mobile users can see that the row can be swiped. Arrows only show on the
// side where there is more content and only while `mediaQuery` matches.
const STYLE_ID = "swipe-arrows-style";

const CSS = `
.swipe-arrow {
  position: absolute;
  z-index: 5;
  display: none;
  width: 36px;
  height: 36px;
  margin-top: -18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 69, 112, 0.85);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}
.swipe-arrow svg { width: 20px; height: 20px; }
.swipe-arrow-left { left: 4px; }
.swipe-arrow-right { right: 4px; }
.swipe-arrow.swipe-arrow-visible { display: flex; }
`;

function injectStyle() {
  if (document.getElementById(STYLE_ID)) {
    return;
  }
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.appendChild(document.createTextNode(CSS));
  document.head.appendChild(style);
}

function createArrow(direction, label) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "swipe-arrow swipe-arrow-" + direction;
  button.setAttribute("aria-label", label);
  const points = direction === "left" ? "14,5 7,12 14,19" : "10,5 17,12 10,19";
  button.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="' + points + '"/></svg>';
  return button;
}

/**
 * @param {HTMLElement} scroller element with overflow-x: auto
 * @param {number} maxWidth arrows are only shown at or below this viewport width
 * @param {string} [anchor] selector of the element the arrows line up with, default the first image
 * @param {string} [edge] "center" (default) or "bottom" of the anchor element
 * @returns {Function} detaches the arrows again
 */
export function attachSwipeArrows(scroller, maxWidth, anchor, edge) {
  if (!scroller || !scroller.parentElement || scroller.__swipeArrows) {
    return function () {};
  }
  injectStyle();

  const host = scroller.parentElement;
  if (window.getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }

  const left = createArrow("left", "Previous");
  const right = createArrow("right", "Next");
  host.appendChild(left);
  host.appendChild(right);

  let resizeObserver = null;
  let observedAnchor = null;
  // Set once the user scrolls the row themselves; until then it is kept at the start.
  let userScrolled = false;
  function markUserScrolled() {
    userScrolled = true;
  }

  const mq = window.matchMedia("(max-width: " + maxWidth + "px)");
  const step = function () {
    return Math.max(scroller.clientWidth * 0.8, 100);
  };

  function update() {
    // Cards render one by one in random order, and with scroll snapping the browser
    // can snap to a later card while they are still empty. Restored scroll positions
    // on reload do the same. Keep the first card in view until the user swipes.
    if (!userScrolled && scroller.scrollLeft !== 0) {
      scroller.scrollLeft = 0;
    }
    const canScroll = mq.matches && scroller.scrollWidth > scroller.clientWidth + 4;
    const atStart = scroller.scrollLeft <= 4;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;
    left.classList.toggle("swipe-arrow-visible", canScroll && !atStart);
    right.classList.toggle("swipe-arrow-visible", canScroll && !atEnd);

    // Align the arrows with `anchor` (default: the first image), falling back to the scroller.
    const image = scroller.querySelector(anchor || "img");
    // Lazy images resize the anchor without resizing the scroller, so watch it too.
    if (resizeObserver && image && image !== observedAnchor) {
      if (observedAnchor) {
        resizeObserver.unobserve(observedAnchor);
      }
      resizeObserver.observe(image);
      observedAnchor = image;
    }
    const hostTop = host.getBoundingClientRect().top;
    const target = (image || scroller).getBoundingClientRect();
    const top = target.top - hostTop + (edge === "bottom" ? target.height : target.height / 2);
    left.style.top = right.style.top = top + "px";
  }

  left.addEventListener("click", function () {
    markUserScrolled();
    scroller.scrollBy({ left: -step(), behavior: "smooth" });
  });
  right.addEventListener("click", function () {
    markUserScrolled();
    scroller.scrollBy({ left: step(), behavior: "smooth" });
  });

  const userEvents = ["touchstart", "pointerdown", "wheel", "keydown"];
  userEvents.forEach(function (type) {
    scroller.addEventListener(type, markUserScrolled, { passive: true });
  });
  scroller.addEventListener("scroll", update, { passive: true });
  scroller.addEventListener("load", update, true); // images finishing to load
  window.addEventListener("resize", update);

  // Content (news, products) is rendered asynchronously.
  const observer = new MutationObserver(update);
  observer.observe(scroller, { childList: true, subtree: true });

  // Card heights change after the images load (the product widget equalises
  // them), which neither the load event nor the mutation observer sees.
  resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
  if (resizeObserver) {
    resizeObserver.observe(scroller);
  }

  scroller.__swipeArrows = true;
  update();

  return function detach() {
    observer.disconnect();
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    userEvents.forEach(function (type) {
      scroller.removeEventListener(type, markUserScrolled);
    });
    scroller.removeEventListener("scroll", update);
    scroller.removeEventListener("load", update, true);
    window.removeEventListener("resize", update);
    host.removeChild(left);
    host.removeChild(right);
    delete scroller.__swipeArrows;
  };
}
